import React from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { useUFOReportError } from '@atlaskit/react-ufo/report-error';
import { fireErrorAnalytics } from '@atlassian/error-handling';

interface HubErrorBoundaryProps {
	children: React.ReactNode;
	fallback: React.ReactElement;
	id: string;
	packageName: string;
	shouldForceError?: boolean;
}

const isFetchClientError = (err: Error) => {
	return (err as unknown as { status: number })?.status < 500;
};

export const HubErrorBoundary = ({
	children,
	packageName,
	id,
	fallback,
	shouldForceError,
}: HubErrorBoundaryProps) => {
	const logError = useUFOReportError();

	const onError = (error: Error) => {
		if (isFetchClientError(error)) {
			// skip fetch client error e.g. 4xx
			return;
		}
		// Fire additional error analytics
		try {
			// Log rUFO error metrics
			logError({
				name: error.name,
				errorMessage: error.message,
				errorStack: error.stack,
				forcedError: shouldForceError,
			});
			fireErrorAnalytics({
				error,
				meta: {
					id,
					packageName,
				},
			});
		} catch (err) {
			// Added to silently fail Uncaught ReferenceError: __SERVER__ is not defined
			// eslint-disable-next-line no-console
			console.warn('Failed to fire error analytics:', err);
		}
	};

	return (
		<ErrorBoundary onError={onError} fallback={fallback}>
			{children}
		</ErrorBoundary>
	);
};
